/**
 * Client-side permission checks.
 *
 * These decide what to *render*. They are not a security boundary and must never be the
 * only thing standing between a user and an action: every one of these codes is enforced
 * again by the API's route guard, which is the check that counts. Hiding a button the
 * user cannot use is a courtesy; the server refusing the request is the control.
 */

export type Action = 'create' | 'read' | 'update' | 'delete'

/** Permission codes arrive as `resource:action`, e.g. `users:create`. */
export function can(permissions: readonly string[], resource: string, action: Action): boolean {
  return permissions.includes(`${resource}:${action}`)
}

/** True if the user can do anything at all with a resource -- used to show nav items. */
export function canAccess(permissions: readonly string[], resource: string): boolean {
  const prefix = `${resource}:`
  return permissions.some((code) => code.startsWith(prefix))
}

/** Group `resource:action` codes into `{ resource: [actions] }` for display. */
export function groupByResource(permissions: readonly string[]): Record<string, Action[]> {
  const grouped: Record<string, Action[]> = {}
  for (const code of permissions) {
    const [resource, action] = code.split(':')
    if (!resource || !action) continue
    ;(grouped[resource] ??= []).push(action as Action)
  }
  return grouped
}
